import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import RemoveIcon from '@mui/icons-material/Remove';
import { IconButton, Typography } from '@mui/material';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button } from 'src/components/Button';
import { CustomFormulaValues } from 'src/components/Forms/CustomFormulaForm';
import { Operator } from 'src/components/Forms/CustomFormulaForm/FormulaCalculator/components/OperatorIcon';
import { Row } from 'src/components/Forms/CustomFormulaForm/FormulaCalculator/components/Row';
import {
  ButtonContainer,
  CalculatorMenu,
  ChipsContainer,
  Loader,
  Wrapper,
} from 'src/components/Forms/CustomFormulaForm/FormulaCalculator/styled';
import {
  flattenGroupedFormulaItems,
  groupFormulaItems,
} from 'src/components/Forms/CustomFormulaForm/FormulaCalculator/utils';
import { ReactComponent as DivideIcon } from 'src/icons/divide.svg';
import { ReactComponent as LeftBraceIcon } from 'src/icons/leftBrace.svg';
import { ReactComponent as RightBraceIcon } from 'src/icons/rightBrace.svg';
import { FormattedCustomFormulaItem } from 'src/utils';
import { CustomFormulaItemType, Metric } from 'src/utils/gql/types';
import { v4 as uuidv4 } from 'uuid';

import { blockMouseEvent } from '../../../../utils/reactUtils';

const calculatorMenuItems = [
  {
    icon: <LeftBraceIcon />,
    value: Operator.LEFT_BRACE,
    label: Operator.LEFT_BRACE,
  },
  {
    icon: <RightBraceIcon />,
    value: Operator.RIGHT_BRACE,
    label: Operator.RIGHT_BRACE,
  },
  {
    icon: <AddIcon />,
    value: Operator.PLUS,
    label: Operator.PLUS,
  },
  {
    icon: <RemoveIcon />,
    value: Operator.MINUS,
    label: Operator.MINUS,
  },
  {
    icon: <CloseIcon />,
    value: Operator.MULTIPLY,
    label: Operator.MULTIPLY,
  },
  {
    icon: <DivideIcon />,
    value: Operator.DIVIDE,
    label: Operator.DIVIDE,
  },
];

const inputItem: FormattedCustomFormulaItem = {
  label: '',
  value: '',
  type: CustomFormulaItemType.metric,
  width: 100,
  id: 'input',
};

interface FormulaCalculatorProps {
  options: Metric[];
  loading?: boolean;
}

const getFormulaItemFromOperator = (value: string) => {
  const validatedOperator = Object.values(Operator).includes(value as Operator)
    ? (value as Operator)
    : Operator.MULTIPLY;

  return {
    value: validatedOperator,
    type: '()'.includes(validatedOperator)
      ? CustomFormulaItemType.brackets
      : CustomFormulaItemType.operator,
    label: validatedOperator,
    width: 0,
    id: uuidv4(),
  };
};

const getMatchingOption = (
  options: Metric[],
  value: string,
): FormattedCustomFormulaItem | null => {
  if (!value) {
    return null;
  }

  const metricOption = options.find((option) =>
    option.label.toLowerCase().startsWith(value.toLowerCase()),
  );

  if (metricOption) {
    return {
      label: metricOption.label,
      value: metricOption.key,
      type: CustomFormulaItemType.metric,
      connectorType: metricOption.type,
      metricId: metricOption.id,
      width: 0,
      id: uuidv4(),
    };
  }

  const numberValue = Number(value.trim());

  if (!isNaN(numberValue)) {
    const stringValue = String(numberValue);
    return {
      label: stringValue,
      value: stringValue,
      type: CustomFormulaItemType.constant,
      width: 0,
      id: uuidv4(),
    };
  }

  const operator = value.match(/[()+\-*/]/)?.[0];

  if (operator) {
    return getFormulaItemFromOperator(operator);
  }

  return null;
};

export const FormulaCalculator: FC<FormulaCalculatorProps> = ({
  options,
  loading,
}) => {
  const chipsContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [chipsContainerWidth, setChipsContainerWidth] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [option, setOption] = useState<FormattedCustomFormulaItem | null>(null);

  const {
    formState: { errors },
    setValue: setFormValue,
    control,
  } = useFormContext<CustomFormulaValues>();

  const formulaItems = useWatch({
    control,
    name: `formulaItems`,
  });

  const rows = groupFormulaItems(formulaItems, chipsContainerWidth);
  const rowsWithInput = groupFormulaItems(
    [...formulaItems, inputItem],
    chipsContainerWidth,
  );
  const error = !!errors.formulaItems?.message;

  const onDragEnd = ({ source, destination }: DropResult) => {
    if (destination === undefined || destination === null) return null;

    if (
      source.droppableId === destination.droppableId &&
      destination.index === source.index
    )
      return null;

    const start = rows[source.droppableId];
    const end = rows[destination.droppableId];

    if (start === end) {
      const newList = start.list.filter(
        (_, idx: number) => idx !== source.index,
      );

      newList.splice(destination.index, 0, start.list[source.index]);

      const newRow = {
        id: start.id,
        list: newList,
      };

      setFormValue(
        'formulaItems',
        flattenGroupedFormulaItems({ ...rows, [newRow.id]: newRow }),
        {
          shouldValidate: true,
        },
      );
    } else {
      const newStartList = start.list.filter(
        (_, idx: number) => idx !== source.index,
      );

      const newStartRow = {
        id: start.id,
        list: newStartList,
      };

      const newEndList = end.list;

      newEndList.splice(destination.index, 0, start.list[source.index]);

      const newEndRow = {
        id: end.id,
        list: newEndList,
      };

      setFormValue(
        'formulaItems',
        flattenGroupedFormulaItems({
          ...rows,
          [newStartRow.id]: newStartRow,
          [newEndRow.id]: newEndRow,
        }),
        { shouldValidate: true },
      );
    }
  };

  const handleOperatorClick = (value: string) => {
    setFormValue(
      'formulaItems',
      [...formulaItems, getFormulaItemFromOperator(value)],
      { shouldValidate: true },
    );

    inputRef.current?.focus();
  };

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);

    setOption(getMatchingOption(options, value));
  };

  const addMetricToFormula = useCallback(() => {
    if (option) {
      const updatedMetrics = [...formulaItems, option];

      setFormValue('formulaItems', updatedMetrics, { shouldValidate: true });
    }

    setInputValue('');
    setOption(null);
  }, [formulaItems, option, setFormValue]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab') {
      addMetricToFormula();
      event.preventDefault();
    }
  };

  const handleAutocompleteOkClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      blockMouseEvent(event);
      addMetricToFormula();
    },
    [addMetricToFormula],
  );

  useEffect(() => {
    const updateDimensions = () => {
      if (chipsContainerRef.current) {
        const width = chipsContainerRef.current.offsetWidth;

        setChipsContainerWidth(width);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  return (
    <Wrapper error={+error}>
      <ChipsContainer ref={chipsContainerRef}>
        {loading ? (
          <Loader />
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            {Object.values(rowsWithInput).map((row, index) => (
              <Row
                ref={inputRef}
                key={row.id}
                row={row}
                hasInput={index === Object.values(rowsWithInput).length - 1}
                inputValue={inputValue}
                onChange={handleChangeInput}
                onKeyDown={handleKeyDown}
                option={option?.label || ''}
              />
            ))}
          </DragDropContext>
        )}

        {option && (
          <ButtonContainer>
            <Button variant="contained" onMouseDown={handleAutocompleteOkClick}>
              Ok
            </Button>
            <Typography>or press Tab</Typography>
            <KeyboardTabIcon />
          </ButtonContainer>
        )}
      </ChipsContainer>

      <CalculatorMenu>
        {calculatorMenuItems.map(({ icon, value }) => (
          <IconButton key={value} onClick={() => handleOperatorClick(value)}>
            {icon}
          </IconButton>
        ))}
      </CalculatorMenu>
    </Wrapper>
  );
};
